import { useMemo } from 'react';
import { Loader2, AlertCircle, Wand2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useCaseMilestones, CaseMilestone } from '@/hooks/useCaseMilestones';
import { useCaseEvents } from '@/hooks/useCaseEvents';
import StageCard, { type StatusUpdateExtra } from './StageCard';
import CaseProgressBar from './CaseProgressBar';
import CasePersonStages from './CasePersonStages';
import { Badge } from '@/components/ui/badge';
import { MilestoneIcon } from '@/utils/milestoneIcons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { getDecisionConfig } from './decisionConfig';


const ASYLUM_SALES_ITEM_ID = 'e35eebb8-d41c-4250-a4e2-ebcd0605bc9d';
const CATEGORY_PRIORITY: Record<string, number> = {
  failed: 5,
  action_required: 4,
  internal_action: 3,
  external_wait: 2,
  completed: 1,
  not_started: 0,
};

type MacroStage = {
  milestone: CaseMilestone;
  isAggregated: boolean;
  completion?: {
    completed: number;
    total: number;
  };
};

function sortByOrder(a: CaseMilestone, b: CaseMilestone) {
  const ao = a.display_order ?? a.milestone_order ?? Number.MAX_SAFE_INTEGER;
  const bo = b.display_order ?? b.milestone_order ?? Number.MAX_SAFE_INTEGER;
  return ao - bo;
}

interface CaseStagesTabProps {
  caseId: string;
  /** Negociação cancelada: etapas congeladas */
  isLocked?: boolean;
}

export default function CaseStagesTab({ caseId, isLocked = false }: CaseStagesTabProps) {
  const {
    milestones,
    caseStages,
    personStagesByContact,
    isLoading,
    toggleMilestoneComplete,
    updateMilestone,
    isUpdating,
    instantiateMilestones,
    isInstantiating,
  } = useCaseMilestones(caseId);

  const { createEvent, eventTypes } = useCaseEvents(caseId);

  const logMilestoneEvent = (milestoneName: string, statusLabel: string, date?: string) => {
    const milestoneEventTypeId = eventTypes.find((t) => t.code === 'milestone_update')?.id;
    if (!milestoneEventTypeId) return;
    createEvent({
      event_type_id: milestoneEventTypeId,
      event_date: date ?? new Date().toISOString().split('T')[0],
      title: `${milestoneName}: ${statusLabel}`,
    }).catch(() => {/* non-blocking */});
  };

  const LOCKED_MSG = 'Processo cancelado — reative a negociação para editar';

  const handleToggle = async (milestone: CaseMilestone) => {
    if (isLocked) { toast.error(LOCKED_MSG); return; }
    const isCompleting = !milestone.completed_date;
    try {
      await toggleMilestoneComplete(milestone);
      const label = isCompleting ? 'Concluído' : 'Reaberto';
      toast.success(isCompleting ? 'Etapa concluída!' : 'Etapa reaberta');
      logMilestoneEvent(milestone.name, label);
    } catch {
      toast.error('Falha ao atualizar etapa');
    }
  };

  const STATUS_LABELS_PT: Record<string, string> = {
    pending: 'Pendente', preparing: 'Em Preparação', submitted: 'Protocolado',
    filed: 'Protocolado', approved: 'Aprovado', denied: 'Negado',
    rfe_received: 'RFE Recebido', rfe_responded: 'RFE Respondido',
    rfi_received: 'RFI Recebido', rfi_responded: 'RFI Respondido',
    receipt_received: 'Recibo Recebido', card_received: 'Cartão Recebido',
    certified: 'Certificado', in_progress: 'Em Andamento', completed: 'Concluído',
    clock_running: 'Relógio Iniciado', i485_filed: 'I-485 Protocolado',
    audit: 'Auditoria', interview_scheduled: 'Entrevista Agendada',
    biometrics_scheduled: 'Biometria Agendada',
  };

  const handleUpdateStatus = async (id: string, status: string, extra?: StatusUpdateExtra) => {
    if (isLocked) { toast.error(LOCKED_MSG); return; }
    try {
      // Reset temporário da etapa (botão de testes): limpa tudo
      if (extra?.resetData) {
        await updateMilestone({
          id,
          updates: {
            current_status: 'pending',
            status_category: 'not_started',
            completed_date: null,
            started_date: null,
            data: {},
          },
        });
        toast.success('Etapa resetada');
        return;
      }

      const updates: Record<string, any> = { current_status: status };
      if (extra?.completed_date !== undefined) updates.completed_date = extra.completed_date;
      if (extra?.data) updates.data = extra.data;
      if (extra?.status_category) updates.status_category = extra.status_category;

      // Generic decision path logic for milestones with decision config
      const milestone = milestones.find((m) => m.id === id);
      if (milestone) {
        const dConfig = getDecisionConfig(milestone.name);
        if (dConfig) {
          const currentData = { ...(milestone.data || {}), ...(updates.data || {}) };
          const intermediateStatuses = dConfig.intermediateOptions.map(o => o.status);
          const finalStatuses = dConfig.finalOptions.map(o => o.status);

          if (intermediateStatuses.includes(status)) {
            currentData.decision_path = status;
          } else if (finalStatuses.includes(status)) {
            if (!intermediateStatuses.includes(currentData.decision_path)) {
              currentData.decision_path = 'direct';
              // Clear stale intermediate fields
              for (const field of dConfig.fieldsToClean) {
                delete currentData[field];
              }
            }
          } else if (['pending', 'preparing', 'submitted'].includes(status)) {
            // Reset decision path when going back to early statuses
            delete currentData.decision_path;
            delete currentData.pwd_decision_path; // backward compat cleanup
            for (const field of dConfig.fieldsToClean) {
              delete currentData[field];
            }
          }
          updates.data = currentData;
        }
      }

      await updateMilestone({ id, updates });

      // Asylum only: when I-589 reaches receipt_received with official_filing_date,
      // sync 150-day clock fields for all per-person EAD milestones.
      const officialFilingDate = (extra?.data?.official_filing_date || extra?.data?.receipt_date) as string | undefined;
      if (status === 'receipt_received' && officialFilingDate) {
        const eligible = new Date(officialFilingDate);
        eligible.setDate(eligible.getDate() + 150);
        const eligibleDate = eligible.toISOString().split('T')[0];

        const allPersonStages = Object.values(personStagesByContact).flatMap(({ milestones: ms }) => ms);
        const eadPersonStages = allPersonStages.filter(
          m =>
            m.template?.sales_item_id === ASYLUM_SALES_ITEM_ID &&
            m.template?.is_per_person === true &&
            m.name === 'EAD (I-765)'
        );

        if (eadPersonStages.length > 0) {
          await Promise.all(
            eadPersonStages.map((ead) =>
              updateMilestone({
                id: ead.id,
                updates: {
                  current_status: 'clock_running',
                  data: {
                    ...(ead.data || {}),
                    clock_start_date: officialFilingDate,
                    clock_eligible_date: eligibleDate,
                  },
                },
              })
            )
          );
        }
      }

      // Auto-log to timeline
      const milestoneName = milestones.find((m) => m.id === id)?.name ?? 'Etapa';
      const eventDate = extra?.completed_date ?? new Date().toISOString().split('T')[0];
      logMilestoneEvent(milestoneName, STATUS_LABELS_PT[status] ?? status, eventDate);

      toast.success('Status atualizado');
    } catch {
      toast.error('Falha ao atualizar status');
    }
  };

  const isAsylumCase = useMemo(
    () => milestones.some((m) => m.template?.sales_item_id === ASYLUM_SALES_ITEM_ID),
    [milestones]
  );

  const macroStages = useMemo<MacroStage[]>(() => {
    const filteredCaseStages =
      isAsylumCase
        ? caseStages.filter((m) => m.name !== 'Relógio EAD (150 Dias)')
        : caseStages;

    if (!isAsylumCase) {
      return filteredCaseStages.sort(sortByOrder).map((milestone) => ({
        milestone,
        isAggregated: false,
      }));
    }

    const byTemplate = new Map<string, CaseMilestone[]>();
    for (const { milestones: personMilestones } of Object.values(personStagesByContact)) {
      for (const milestone of personMilestones) {
        const key = milestone.milestone_template_id ?? milestone.name;
        if (!byTemplate.has(key)) byTemplate.set(key, []);
        byTemplate.get(key)!.push(milestone);
      }
    }

    const aggregatedStages: MacroStage[] = Array.from(byTemplate.values()).map((group) => {
      const orderedGroup = [...group].sort(sortByOrder);
      const base = orderedGroup[0];
      const total = orderedGroup.length;
      const completed = orderedGroup.filter(
        (m) => m.status_category === 'completed' || m.current_status === 'completed' || !!m.completed_date
      ).length;

      let statusCategory: CaseMilestone['status_category'] = 'not_started';
      let currentStatus: CaseMilestone['current_status'] = 'pending';

      if (completed === total) {
        statusCategory = 'completed';
        currentStatus = 'completed';
      } else if (completed > 0) {
        statusCategory = 'internal_action';
        currentStatus = 'in_progress';
      }

      return {
        milestone: {
          ...base,
          id: `macro-${base.milestone_template_id ?? base.name}`,
          contact_id: null,
          status_category: statusCategory,
          current_status: currentStatus,
          started_date: null,
          completed_date: completed === total ? base.completed_date : null,
          data: {},
          notes: null,
        },
        isAggregated: true,
        completion: { completed, total },
      };
    });

    return [...filteredCaseStages, ...aggregatedStages.map((s) => s.milestone)]
      .sort(sortByOrder)
      .map((milestone) => {
        const aggregated = aggregatedStages.find((s) => s.milestone.id === milestone.id);
        if (aggregated) return aggregated;
        return { milestone, isAggregated: false };
      });
  }, [caseStages, isAsylumCase, personStagesByContact]);

  const progressMilestones = useMemo(() => {
    if (isAsylumCase) {
      return macroStages.map((stage) => stage.milestone);
    }

    // Preserve existing behavior for non-Asylum:
    // case-level stages + one representative per per-person template using worst category.
    const byTemplate = new Map<string, CaseMilestone[]>();
    for (const { milestones: personMilestones } of Object.values(personStagesByContact)) {
      for (const milestone of personMilestones) {
        const key = milestone.milestone_template_id ?? milestone.name;
        if (!byTemplate.has(key)) byTemplate.set(key, []);
        byTemplate.get(key)!.push(milestone);
      }
    }

    const personRepresentatives = Array.from(byTemplate.values()).map((group) =>
      group.reduce((best, curr) => {
        const bp = CATEGORY_PRIORITY[best.status_category ?? 'not_started'] ?? 0;
        const cp = CATEGORY_PRIORITY[curr.status_category ?? 'not_started'] ?? 0;
        return cp > bp ? curr : best;
      })
    );

    return [...caseStages, ...personRepresentatives].sort(sortByOrder);
  }, [caseStages, isAsylumCase, macroStages, personStagesByContact]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleInstantiate = async () => {
    try {
      await instantiateMilestones();
      toast.success('Etapas geradas com sucesso!');
    } catch {
      toast.error('Falha ao gerar etapas. Verifique se o catálogo está configurado.');
    }
  };

  if (milestones.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        <AlertCircle className="w-14 h-14 mx-auto mb-4 opacity-40" />
        <p className="font-medium">Nenhuma etapa configurada</p>
        <p className="text-sm mt-1">As etapas aparecerão aqui quando o processo estiver configurado</p>
        <Button
          className="mt-4"
          onClick={handleInstantiate}
          disabled={isInstantiating}
        >
          {isInstantiating ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Wand2 className="w-4 h-4 mr-2" />
          )}
          Gerar Etapas do Processo
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Progress bar with named steps — one entry per template */}
      <CaseProgressBar milestones={progressMilestones} isCancelled={isLocked} />

      {/* Macro stages */}
      {macroStages.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
            Etapas do Processo
          </h3>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            {macroStages.map((stage, idx) => (
              stage.isAggregated ? (
                <div
                  key={stage.milestone.id}
                  className="relative overflow-hidden border rounded-xl bg-card shadow-sm"
                >
                  <div className="flex items-center justify-between px-4 pt-4 pb-1">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-[34px] h-[34px] rounded-lg flex items-center justify-center flex-shrink-0 bg-muted">
                        <MilestoneIcon icon={stage.milestone.template?.icon} size={16} />
                      </div>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-sm font-semibold text-foreground truncate">{stage.milestone.name}</span>
                        {stage.milestone.description && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                aria-label="Informações da etapa"
                                className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:text-foreground transition-colors shrink-0"
                              >
                                <Info className="w-3.5 h-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs whitespace-normal text-xs leading-relaxed">
                              {stage.milestone.description}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    <Badge
                      className={`border-0 text-[10px] font-semibold px-2.5 py-0.5 rounded-full ${
                        stage.completion?.completed === stage.completion?.total
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                          : stage.completion?.completed
                          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {stage.completion?.completed === stage.completion?.total
                        ? 'Concluído'
                        : stage.completion?.completed
                        ? 'Em andamento'
                        : 'Pendente'}
                    </Badge>
                  </div>
                  <div className="px-4 pb-3">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                      Progresso por pessoa
                    </div>
                    <div className="text-sm font-medium text-foreground">
                      {stage.completion?.completed ?? 0}/{stage.completion?.total ?? 0}
                    </div>
                  </div>
                </div>
              ) : (
                <StageCard
                  key={stage.milestone.id}
                  milestone={stage.milestone}
                  index={idx}
                  onToggleComplete={handleToggle}
                  onUpdateStatus={handleUpdateStatus}
                  isUpdating={isUpdating}
                  isLocked={isLocked}
                />
              )
            ))}
          </div>
        </div>
      )}

      {/* Per-person stages */}
      <CasePersonStages
        personStagesByContact={personStagesByContact}
        onToggleComplete={handleToggle}
        onUpdateStatus={handleUpdateStatus}
        isUpdating={isUpdating}
        isLocked={isLocked}
      />
    </div>
  );
}
